import React, { FunctionComponent, useState } from 'react';
import {
  GestureResponderEvent, ImageBackground, SafeAreaView, StyleProp, StyleSheet,
  Text, TextStyle, TouchableOpacity, View, ViewStyle,
} from 'react-native';
import { Icon } from '../../components';
import Theme from '../../theme';
import { LinearGradient, SkeletonLoader } from '../../ui/components';
import { defaultScale } from '../../utils/scaling';

interface IProps {
  title: string;
  subtitle?: string;
  description?: string;
  icon?: JSX.Element;
  actions: React.ReactNode;
  containerStyle?: StyleProp<ViewStyle>;
  titleStyle?: StyleProp<TextStyle>;
  subTitleStyle?: StyleProp<ViewStyle>;
  imageSource: any;
  useGradient?: boolean;
  gradientHeight?: string;
  gradientOpacity?: number;
  onClose: ((event: GestureResponderEvent) => void) | undefined;
  contentContainerStyle?: StyleProp<ViewStyle>;
  closeIconContainerStyle?: StyleProp<ViewStyle>;
  gradientWebClassName?: string;
}
export const BackgroundImageHeader: FunctionComponent<IProps> = ({
  title,
  subtitle,
  description,
  icon,
  actions,
  containerStyle,
  titleStyle,
  subTitleStyle,
  useGradient,
  imageSource,
  gradientHeight = '100%',
  gradientOpacity = 0.6,
  contentContainerStyle,
  closeIconContainerStyle,
  gradientWebClassName,
  onClose,
}) => {
  const [isLoadingImage, setIsLoadingImage] = useState(true);

  const closePressed = (e: GestureResponderEvent) => {
    e.preventDefault();
    if (onClose) {
      onClose(e);
    }
  };

  return (
    <View style={[!containerStyle && styles.container, containerStyle]}>
      <ImageBackground
        source={imageSource}
        style={[styles.image]}
        resizeMode='cover'
        onLoadStart={() => {
          setIsLoadingImage(true);
        }}
        onLoadEnd={() => {
          setIsLoadingImage(false);
        }}
      >
        <>
          {!isLoadingImage && <SafeAreaView style={{ flex: 0 }} />}
          {isLoadingImage && <SkeletonLoader containerStyle={containerStyle} />}
          <TouchableOpacity onPress={closePressed} style={[styles.closeIconContainerStyle, closeIconContainerStyle]}>
            <Icon name='md-close' color={Theme.WHITE} size={defaultScale(35)} />
          </TouchableOpacity>
          {!isLoadingImage && <View style={[styles.contentContainer, contentContainerStyle]}>
            {icon ? (
              <View style={styles.iconTitleContainer}>
                {icon}
                <Text style={[styles.title, titleStyle]}>{title}</Text>
                </View>
            ) : (
              <Text style={[styles.title, titleStyle]}>{title}</Text>
            )}
            {
              subtitle &&
              <Text style={[styles.subtitle, subTitleStyle]}>{subtitle}</Text>
            }
            {
              description &&
              <Text style={styles.description}>{description}</Text>
            }
            {actions}
          </View>}
          {
            useGradient && !isLoadingImage &&
            <LinearGradient
              colors={['transparent', Theme.BLACK]}
              style={[styles.gradient, { height: gradientHeight, opacity: gradientOpacity }]}
              webClassName={gradientWebClassName}
            />}
        </>
      </ImageBackground>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    height: defaultScale(350),
  },
  image: {
    width: '100%',
    height: '100%',
  },
  contentContainer: {
    zIndex: 10,
    flex: 1,
    justifyContent: 'flex-end',
    marginVertical: defaultScale(10),
    marginHorizontal: defaultScale(12),
    paddingBottom: defaultScale(15),
  },
  iconTitleContainer: {
    display: 'flex',
    flexDirection: 'row',
  },
  title: {
    ...Theme.textStyle.heading,
    color: Theme.WHITE,
    paddingVertical: defaultScale(5),
  },
  subtitle: {
    fontSize: defaultScale(16),
    color: Theme.WHITE,
    fontWeight: '400',
    marginTop: defaultScale(8),
    marginBottom: defaultScale(14),
  },
  description: {
    fontSize: defaultScale(18),
    color: Theme.WHITE,
    fontWeight: '400',
    marginTop: defaultScale(8),
    marginBottom: defaultScale(15),
  },
  gradient: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    left: 0,
  },
  closeIconContainerStyle: {
    alignSelf: 'flex-end',
    marginHorizontal: defaultScale(15),
    zIndex: 10,
  },
});
